import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, CheckCircle2, AlertCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SurveyPublicPage() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [answers, setAnswers] = useState({});
  const [identity, setIdentity] = useState({ employee_email: "", employee_name: "", matricula: "" });

  useEffect(() => {
    axios.get(`${API_BASE}/api/public/surveys/${token}`)
      .then((r) => { setSurvey(r.data); })
      .catch((e) => { setError(e.response?.data?.detail || "Pesquisa não disponível"); })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!survey) return;
    // Validate required
    for (const q of survey.questions) {
      if (q.required && (answers[q.key] === undefined || answers[q.key] === "" || answers[q.key] === null)) {
        return alert(`Responda: ${q.label}`);
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...(survey.anonymous ? {} : identity),
        answers,
      };
      await axios.post(`${API_BASE}/api/public/surveys/${token}/responses`, payload);
      setDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao enviar resposta");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-8"><Skeleton className="h-[400px] max-w-[640px] mx-auto" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 grid place-items-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-[480px]" data-testid="survey-error">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
          <h1 className="font-display text-xl font-semibold mb-2">Pesquisa indisponível</h1>
          <p className="text-[13px] text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-[#FAF4E5] grid place-items-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-[520px]" data-testid="survey-done">
          <div className="h-20 w-20 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-3">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-semibold mb-2">Resposta enviada com sucesso!</h1>
          <p className="text-[14px] text-muted-foreground">Sua opinião é muito valiosa para nós. Obrigado pelo tempo dedicado.</p>
          <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">TheoKratos RH</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF4E5] via-white to-slate-50 py-8 px-4" data-testid="survey-public-page">
      <form onSubmit={submit} className="max-w-[680px] mx-auto bg-white rounded-2xl shadow-xl border border-border overflow-hidden">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-7 text-white">
          <div aria-hidden className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#C5A059]/30 blur-[60px]" />
          <div className="relative">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Pesquisa
            </div>
            <h1 className="mt-1 font-display text-[24px] sm:text-[28px] font-semibold leading-tight">{survey.title}</h1>
            {survey.description && <p className="mt-1.5 text-[13px] text-white/75">{survey.description}</p>}
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10.5px] bg-white/10 px-2 py-1 rounded">
              {survey.anonymous ? "Resposta 100% anônima" : "Identifique-se com seus dados"}
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-8 space-y-5">
          {/* Identity */}
          {!survey.anonymous && (
            <div className="rounded-lg bg-slate-50 border border-border p-4 space-y-3" data-testid="identity-form">
              <div className="text-[11px] uppercase tracking-wider text-[#8A6B2B] font-semibold">Seus dados</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px]">Nome completo</Label>
                  <Input value={identity.employee_name} onChange={(e) => setIdentity({ ...identity, employee_name: e.target.value })}
                         data-testid="identity-name" />
                </div>
                <div>
                  <Label className="text-[11px]">Matrícula</Label>
                  <Input value={identity.matricula} onChange={(e) => setIdentity({ ...identity, matricula: e.target.value })}
                         data-testid="identity-matricula" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[11px]">E-mail (para evitar respostas duplicadas)</Label>
                  <Input type="email" value={identity.employee_email}
                         onChange={(e) => setIdentity({ ...identity, employee_email: e.target.value })}
                         data-testid="identity-email" />
                </div>
              </div>
            </div>
          )}

          {/* Questions */}
          {survey.questions.map((q, i) => (
            <Question
              key={q.key}
              index={i + 1}
              question={q}
              value={answers[q.key]}
              onChange={(v) => setAnswers({ ...answers, [q.key]: v })}
            />
          ))}

          <Button type="submit" disabled={submitting}
                  className="w-full h-12 bg-[#234935] hover:bg-[#2c5c43] text-white font-semibold text-[14px]"
                  data-testid="submit-survey">
            {submitting ? "Enviando..." : "Enviar resposta"}
          </Button>

          <div className="text-center text-[10.5px] text-muted-foreground">
            TheoKratos RH · suas respostas são tratadas com confidencialidade
          </div>
        </div>
      </form>
    </div>
  );
}

function Question({ index, question, value, onChange }) {
  return (
    <div className="rounded-lg border border-border p-4 bg-white" data-testid={`q-${question.key}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="h-7 w-7 shrink-0 rounded-full bg-[#234935] text-white grid place-items-center text-[12px] font-semibold">
          {index}
        </div>
        <div className="flex-1">
          <div className="font-medium text-[13.5px] leading-snug">{question.label}</div>
          {question.required && <span className="text-[10px] text-rose-600 font-medium">* obrigatória</span>}
        </div>
      </div>
      <div className="pl-10">
        <QuestionInput question={question} value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function QuestionInput({ question, value, onChange }) {
  const t = question.type;

  if (t === "open") {
    return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
                     placeholder="Sua resposta..." data-testid={`input-${question.key}`} />;
  }
  if (t === "scale_5" || t === "stars") {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}
                  className={`h-10 w-10 rounded-lg text-[13px] font-semibold transition-all ${
                    value === n ? "bg-[#234935] text-white shadow-md scale-110"
                                : "bg-white border-2 border-border text-slate-600 hover:border-[#C5A059]"
                  }`}
                  data-testid={`input-${question.key}-${n}`}>
            {n}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground ml-2">
          {value ? ["", "Insatisfeito", "Pouco satisfeito", "Neutro", "Satisfeito", "Muito satisfeito"][value] : "Selecione"}
        </span>
      </div>
    );
  }
  if (t === "scale_10") {
    return (
      <div className="grid grid-cols-10 gap-1">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}
                  className={`h-9 rounded text-[12px] font-semibold ${
                    value === n ? "bg-[#234935] text-white"
                                : "bg-white border border-border text-slate-600 hover:border-[#C5A059]"
                  }`}>
            {n}
          </button>
        ))}
      </div>
    );
  }
  if (t === "nps") {
    return (
      <div>
        <div className="grid grid-cols-11 gap-1">
          {Array.from({ length: 11 }, (_, i) => i).map((n) => (
            <button key={n} type="button" onClick={() => onChange(n)}
                    className={`h-9 rounded text-[12px] font-semibold ${
                      value === n ? "bg-[#234935] text-white"
                                  : n <= 6 ? "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                                  : n <= 8 ? "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
                                  : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    }`}
                    data-testid={`input-${question.key}-${n}`}>
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-muted-foreground">
          <span>Nada provável</span>
          <span>Muito provável</span>
        </div>
      </div>
    );
  }
  if (t === "yesno") {
    return (
      <div className="flex gap-2">
        {["sim", "nao"].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
                  className={`px-5 h-10 rounded-lg text-[12.5px] font-medium ${
                    value === v ? "bg-[#234935] text-white"
                                : "bg-white border-2 border-border text-slate-600 hover:border-[#C5A059]"
                  }`}>
            {v === "sim" ? "Sim" : "Não"}
          </button>
        ))}
      </div>
    );
  }
  if (t === "single") {
    return (
      <div className="space-y-1.5">
        {(question.options || []).map((opt) => (
          <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-all ${
            value === opt ? "border-[#234935] bg-[#234935]/5" : "border-border hover:bg-slate-50"
          }`}>
            <input type="radio" checked={value === opt} onChange={() => onChange(opt)}
                   className="accent-[#234935]" />
            <span className="text-[12.5px]">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (t === "multi") {
    const list = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      onChange(list.includes(opt) ? list.filter((x) => x !== opt) : [...list, opt]);
    };
    return (
      <div className="space-y-1.5">
        {(question.options || []).map((opt) => (
          <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${
            list.includes(opt) ? "border-[#234935] bg-[#234935]/5" : "border-border hover:bg-slate-50"
          }`}>
            <input type="checkbox" checked={list.includes(opt)} onChange={() => toggle(opt)}
                   className="accent-[#234935]" />
            <span className="text-[12.5px]">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}
